/*
 Order utils to process order related data  
 */

import { ACCOUNT, HYPHEN, ORDER_ID } from '@/data/constants/common';
import {
	CARD_PAYMENT_METHODS,
	CASH_PAYMENT_METHODS,
	INVENTORY_STATUS,
	PROPS_TO_MAP_FOR_CATENTRY,
	SHIP_MODE_CODE,
} from '@/data/constants/order';
import { OrderItem } from '@/data/types/Order';

/**
 * Updates the order item object with properties from the catalog entry.
 * @param orderItemObj The order item object to update.
 * @param catentry The catalog entry data.
 */
const updateObjectWithCatentry = (orderItemObj: any, catentry: any) => {
	PROPS_TO_MAP_FOR_CATENTRY.forEach((prop) => {
		if (catentry[prop] !== undefined) orderItemObj[prop] = catentry[prop];
	});

	if (catentry.price !== undefined && catentry.isB2B) {
		const regularPrice = catentry.price.find((p: any) => p.usage === 'Offer');
		orderItemObj['regularPrice'] = regularPrice ? regularPrice?.value : '';
	}

	const { seller, sellerId } = catentry;
	Object.assign(orderItemObj, { seller, sellerId });
};

/**
 * Checks if recurring order is disallowed for a specific item based on catalog entry.
 * @param orderItemObj The order item object.
 * @param catentry The catalog entry data.
 * @returns True if recurring order is disallowed, false otherwise.
 */
const checkRecurringOrder = (orderItemObj: any, catentry: any) => {
	if (catentry.disallowRecurringOrder !== undefined) {
		orderItemObj['disallowRecurringOrder'] = catentry.disallowRecurringOrder;
		if (catentry.disallowRecurringOrder === '1') {
			return true;
		}
	}
	return false;
};

/**
 * Processes a single order item, checking inventory status and merging catalog entry data.
 * @param item The order item to process.
 * @param newCatentries Map of catalog entries.
 * @param checkInventory Boolean flag to check inventory status.
 * @returns An object containing the processed item and flags for checkout/recurring disabilities.
 */
const processSingleOrderItem = (item: OrderItem, newCatentries: any, checkInventory: boolean) => {
	let itemCheckoutDisabled = false;
	let itemRecurringDisabled = false;

	if (
		checkInventory &&
		item.orderItemInventoryStatus !== INVENTORY_STATUS.available &&
		item.orderItemInventoryStatus !== INVENTORY_STATUS.allocated
	) {
		itemCheckoutDisabled = true;
	}
	const obj = { ...item };
	const catentryId = item.productId;
	if (newCatentries !== null) {
		const catentry = newCatentries[catentryId];
		if (catentry !== undefined) {
			updateObjectWithCatentry(obj, catentry);
			itemRecurringDisabled = checkRecurringOrder(obj, catentry);
		}
	}
	return { obj, itemCheckoutDisabled, itemRecurringDisabled };
};

/**
 * Processes the cart data response.
 * Calculates number of items, processes individual order items, and determines if checkout or recurring orders are disabled.
 * @param response The cart response object.
 * @param newCatentries Optional map of catalog entries.
 * @param checkInventory Optional flag to check inventory.
 * @returns Processed cart data including order items and status flags.
 */
export const processCartData = (response: any, newCatentries?: any, checkInventory?: boolean) => {
	if (!response) return null;

	const { orderItem: orderItems, ...cart } = response;
	const numItems = orderItems
		? orderItems.reduce((c: number, item: OrderItem) => +item.quantity + c, 0)
		: 0;

	let newOrderItems: OrderItem[] = [];
	let isCheckoutDisabled = false;
	let isRecurringOrderDisabled = false;

	if (orderItems && orderItems.length > 0) {
		newOrderItems = orderItems.map((item: OrderItem) => {
			const { obj, itemCheckoutDisabled, itemRecurringDisabled } = processSingleOrderItem(
				item,
				newCatentries,
				!!checkInventory
			);
			if (itemCheckoutDisabled) isCheckoutDisabled = true;
			if (itemRecurringDisabled) isRecurringOrderDisabled = true;
			return obj;
		});
	}

	if (isRecurringOrderDisabled && cart && cart.orderId) {
		if (localStorage.getItem(ACCOUNT + HYPHEN + ORDER_ID + HYPHEN + cart.orderId)) {
			const recurringOrderInfo: any[] = [false, '0', null];
			localStorage.setItem(
				ACCOUNT + HYPHEN + ORDER_ID + HYPHEN + cart.orderId,
				JSON.stringify(recurringOrderInfo)
			);
		}
	}

	return {
		cart,
		numItems,
		orderItems: newOrderItems,
		isCheckoutDisabled,
		isRecurringOrderDisabled,
	};
};

/**
 * Checks if checkout is disabled based on order items inventory status.
 * @param orderItems List of order items.
 * @returns True if any item has invalid inventory status.
 */
export const checkIsCheckoutDisabled = (orderItems: OrderItem[]) => {
	if (orderItems && orderItems.length > 0) {
		return orderItems.some(
			(item: OrderItem) =>
				item.orderItemInventoryStatus !== INVENTORY_STATUS.available &&
				item.orderItemInventoryStatus !== INVENTORY_STATUS.allocated
		);
	}
	return false;
};

/**
 * Processes and sorts usable payment information.
 * Separates payment methods into cards and cash/others, returning a concatenated list with cards first.
 * @param response The response containing usablePaymentInformation.
 * @returns Sorted array of payment methods.
 */
export const processPaymentMethodsData = (response: any) => {
	if (response && response.usablePaymentInformation) {
		const { cardsList, cashList } = response.usablePaymentInformation.reduce(
			(acc: { cardsList: any[]; cashList: any[] }, payment: any) => {
				if (CARD_PAYMENT_METHODS.includes(payment.paymentMethodName)) {
					acc.cardsList.push(payment);
				} else if (CASH_PAYMENT_METHODS.includes(payment.paymentMethodName)) {
					acc.cashList.push(payment);
				}
				return acc;
			},
			{ cardsList: [], cashList: [] }
		);

		if (cardsList.length > 0) {
			return cardsList.concat(cashList);
		} else {
			return cashList;
		}
	}
	return [];
};

/**
 * Filters usable shipping modes.
 * Removes 'PickUp' ship mode from the list.
 * @param response The response containing usableShippingMode.
 * @returns Filtered array of shipping modes.
 */
export const processShipModesData = (response: any) => {
	if (response && response.usableShippingMode) {
		return response.usableShippingMode.filter(
			(s: any) => s.shipModeCode !== SHIP_MODE_CODE.PICK_UP_IN_STORE
		);
	}
	return [];
};
